import { Typography } from '@mui/material';
import { addMonths, startOfToday } from 'date-fns';
import { required, useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { ShowSection } from '@sh4res/bo-common/components';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { inputDisabledProps } from '@sh4res/bo-common/utils';
import { tryToLocalDate } from '@sh4res/bo-common/utils/date';

const testId = 'BoExternalB2bCampaignConsultationPeriodSection';

type Props = {
  isEditionDisabled: boolean;
  isStartDateEditionDisabled?: boolean;
  hasDescription?: boolean;
};

export const ConsultationPeriodSection = (props: Props) => {
  const {
    isEditionDisabled,
    isStartDateEditionDisabled = isEditionDisabled,
    hasDescription = true,
  } = props;
  const translate = useTranslate();
  const form = useFormContext();

  const startDate = useWatch({ name: 'start_date' });
  const endDate = useWatch({ name: 'end_date' });
  const choiceCollectedBy = useWatch({ name: 'choice_collected_by' });

  if (choiceCollectedBy === 'company') {
    return null;
  }

  return (
    <ShowSection
      leftAligned
      label="resources.bo_common.b2b.campaigns.sections.allocations.consultation_period"
      noCard={true}>
      {hasDescription && (
        <Typography>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.allocations.consultation_period_description',
          )}
        </Typography>
      )}

      <DateInput
        data-testid={`${testId}_StartDateInput`}
        source="start_date"
        isRequired
        defaultValue={startDate}
        validate={
          isStartDateEditionDisabled
            ? undefined
            : [
                required(),
                (value, allValues) => {
                  const date = tryToLocalDate(value);
                  const endDateValue = tryToLocalDate(allValues.end_date);
                  if (date && date < startOfToday()) {
                    return translate('ra.validation.minValue', {
                      min: startOfToday().toLocaleDateString(),
                    });
                  }
                  if (date && endDateValue && date > endDateValue) {
                    return translate('ra.validation.maxValue', { max: allValues.end_date });
                  }
                },
              ]
        }
        onChange={async () => form.trigger('end_date')}
        {...inputDisabledProps(isStartDateEditionDisabled)}
      />
      <DateInput
        data-testid={`${testId}_EndDateInput`}
        source="end_date"
        isRequired
        defaultValue={endDate}
        validate={
          isEditionDisabled
            ? undefined
            : [
                required(),
                (value, allValues) => {
                  const date = tryToLocalDate(value);
                  const startDateValue = tryToLocalDate(allValues.start_date);
                  if (date && date < startOfToday()) {
                    return translate(
                      'resources.bo_common.b2b.campaigns.sections.allocations.end_date_after_today',
                    );
                  }
                  if (date && startDateValue) {
                    if (date < startDateValue) {
                      return translate('ra.validation.minValue', { min: allValues.start_date });
                    }
                    if (date > addMonths(startDateValue, 3)) {
                      return translate(
                        'resources.bo_common.b2b.campaigns.sections.allocations.consultation_period_3_month',
                      );
                    }
                  }
                },
              ]
        }
        onChange={async () => form.trigger('start_date')}
        {...inputDisabledProps(isEditionDisabled)}
      />
    </ShowSection>
  );
};
